<template>
  <div v-if="visible" class="ocv" aria-label="Offset check">
    <div class="ocv__head">
      <span class="ocv__title">Offset check</span>
      <span class="ocv__point">{{ point.label }}</span>
      <span class="ocv__units">site metres</span>
    </div>

    <div class="ocv__panes">
      <article v-for="pane in panes" :key="pane.key" class="ocv__pane">
        <header class="ocv__pane-head">
          <span class="ocv__pane-title">{{ pane.title }}</span>
          <span class="ocv__pane-src">{{ pane.source }}</span>
        </header>

        <!-- Plan-view schematic. Hollow marker = raw pick, filled crosshair =
             adjusted position, arrow = the offset that moved it. Both panes are
             drawn at ONE shared scale (see `scale`) so their arrows are directly
             comparable; scaled per-pane, a 5 mm and a 500 mm offset would look
             identical, which is the exact comparison this view exists for. -->
        <svg class="ocv__plan" viewBox="0 0 120 120" role="img" :aria-label="pane.planLabel">
          <line class="ocv__hair" x1="12" y1="60" x2="108" y2="60" />
          <line class="ocv__hair" x1="60" y1="12" x2="60" y2="108" />
          <text class="ocv__compass" x="60" y="10" text-anchor="middle">N</text>
          <text class="ocv__compass" x="112" y="63" text-anchor="middle">E</text>

          <line
            v-if="pane.hasPlanShift"
            class="ocv__arrow"
            x1="60"
            y1="60"
            :x2="pane.tipX"
            :y2="pane.tipY"
          />
          <circle class="ocv__raw" cx="60" cy="60" r="4" />
          <g class="ocv__adj" :transform="`translate(${pane.tipX}, ${pane.tipY})`">
            <line x1="-7" y1="0" x2="7" y2="0" />
            <line x1="0" y1="-7" x2="0" y2="7" />
            <circle cx="0" cy="0" r="2.5" />
          </g>
        </svg>

        <!-- Elevation is the axis a plan view cannot show, so it gets its own
             gauge rather than being left to the numbers alone. -->
        <div class="ocv__elev">
          <span class="ocv__elev-cap">Elev</span>
          <div class="ocv__elev-track">
            <span class="ocv__elev-zero"></span>
            <span class="ocv__elev-bar" :style="pane.elevBarStyle"></span>
          </div>
          <span class="ocv__elev-val">{{ pane.offsetText.z }}</span>
        </div>

        <dl class="ocv__rows">
          <div v-for="row in pane.rows" :key="row.axis" class="ocv__row">
            <dt class="ocv__axis">{{ row.axis }}</dt>
            <dd class="ocv__raw-val">{{ row.raw }}</dd>
            <dd class="ocv__off-val">{{ row.offset }}</dd>
            <dd class="ocv__adj-val">{{ row.adjusted }}</dd>
          </div>
        </dl>

        <footer class="ocv__legend">
          <span>raw</span>
          <span>offset</span>
          <span>adjusted</span>
        </footer>
      </article>
    </div>
  </div>
</template>

<script>
/**
 * AVA-475 Step 4 — dual-offset visual confirmation.
 *
 * Renders ONLY when the active point carries a non-zero offset on BOTH the
 * design and the as-built side (`activePoint.showOffsetCheck`, derived in
 * VERIFICATION/GET_POINTS_WITH_RESULTS). When both sides are shifted, the
 * deviation compares two adjusted positions and neither one is what was
 * actually picked — so the surveyor is shown where each adjustment landed.
 *
 * DELIBERATE OMISSIONS (explicit ticket requirement — do not "improve"):
 *   no warning copy, no confirm button, no dismiss button.
 * It is a read-only aid. It appears when the condition holds and disappears
 * when it stops holding; there is nothing to acknowledge.
 *
 * DESIGN DECISION — this is a SCHEMATIC, not a second Forge viewer.
 * "Design on the model / as-built on the scan" reads like two live viewers, but
 * the ticket asks only for a visual aid with no controls. A second LMV instance
 * would duplicate the preflight, isolation and crop-handle state this feature
 * already fights (see .claude/features/AVA-475_SPIKE_FINDINGS.md) for no gain
 * the ticket asks for. Revisit only if a real requirement needs live geometry.
 *
 * UNITS — THE FOURTH `viewerToSite` LEAF (utils/modelUnits.js header).
 *
 * Everything reaching this component from the store is in VIEWER units: `design`
 * and `asBuilt` because that is what the pick returned, the offsets because
 * `adjustPoint` sums them straight into those points (pointMath.js), and
 * `designAdjusted`/`asBuiltAdjusted` because they are that sum. On a millimetre
 * facility a viewer unit is a millimetre, so printing any of them through
 * `formatMetres` unconverted labels 25190 mm as "25190.0000 m".
 *
 * ALL THREE COLUMNS CROSS, and they must cross together. Converting only the
 * adjusted column would seat raw viewer units beside site metres under one
 * caption — worse than the original bug, where the three were at least wrong
 * consistently and their arithmetic still held.
 *
 * TWO CONVERTERS, BECAUSE TWO KINDS OF QUANTITY. Raw and adjusted are POSITIONS
 * and take `viewerToSite`, which applies the model's `globalOffset`. An offset is
 * a DIFFERENCE and takes `viewerVectorToSite`, which is scale-only: the constant
 * offset cancels in a subtraction, and applying it here would add tens of
 * thousands of metres to a five-millimetre correction. That is also why this
 * component is the FOURTH entry on the leaf list and not the fifth — a vector is
 * a magnitude, not a claim about where something is.
 *
 * The previous note here cited `DesignPointModal`'s "Offset (optional, m)" label
 * as evidence the offsets were already metres. That modal is UNMOUNTED (zero
 * imports in src/, AVA-475 W3.4), so the claim rested on dead code; the live
 * evidence is `adjustPoint`, which can only be summing one space.
 *
 * A null transform renders every number as an em dash and draws no arrow. The
 * panel then says "not known yet" instead of "the offset is zero".
 */
import { mapGetters } from 'vuex';
import { formatMetres } from '@/features/Verification/utils/pointMath';
import { viewerToSite, viewerVectorToSite } from '@/features/Verification/utils/modelUnits';

/** Longest arrow drawn, in viewBox units. The plan area is 120x120. */
const MAX_ARROW = 34;
/** Tallest elevation bar, as a percentage of the gauge's half-height. */
const MAX_ELEV_BAR = 100;

const AXES = [
  { key: 'x', label: 'E' },
  { key: 'y', label: 'N' },
  { key: 'z', label: 'EL' },
];

/**
 * Metre-scale position, on the shared precision so this panel cannot disagree
 * with the report or the result card about the same coordinate.
 */
function fmtPos(value) {
  return formatMetres(value, { ascii: true });
}

/**
 * Signed metre-scale offset. `signed` because an offset is a change, not a
 * position. ASCII sign — this is DOM, but these values get copied out of the
 * panel into notes and spreadsheets, and a U+2212 pasted into a numeric cell is
 * text rather than a number.
 */
function fmtOffset(value) {
  return formatMetres(value, { ascii: true, signed: true });
}

export default {
  name: 'OffsetCheckView',
  props: {
    /**
     * `{ mmPerViewerUnit, siteScale, siteOffset }` from `GET_UNIT_TRANSFORM`,
     * threaded by WorkspacePanel — the same route ResultsPanel uses for
     * PointResultCard, so the two position surfaces cannot end up reading
     * different transforms. Null until the pre-flight resolves the model.
     */
    unitTransform: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters('VERIFICATION', {
      point: 'GET_ACTIVE_POINT',
    }),

    visible() {
      return !!this.point?.showOffsetCheck;
    },

    /**
     * Both offsets in SITE metres — converted ONCE, here, because the arrows,
     * the elevation gauge and the printed numbers all derive from them. Convert
     * them separately and a rounding or a converter choice can drift between
     * what the panel draws and what it says.
     */
    siteOffsets() {
      return {
        design: viewerVectorToSite(this.point?.designOffset, this.unitTransform),
        asBuilt: viewerVectorToSite(this.point?.asBuiltOffset, this.unitTransform),
      };
    },

    /**
     * ONE scale for both panes, driven by the larger plan-view offset. Without
     * this the two arrows are normalised independently and the panes stop being
     * comparable — which would defeat the whole point of showing them together.
     */
    scale() {
      if (!this.visible) return 0;
      const { design, asBuilt } = this.siteOffsets;
      const magnitudes = [design, asBuilt].map((o) => Math.hypot(o?.x || 0, o?.y || 0));
      const largest = Math.max(...magnitudes);
      return largest > 0 ? MAX_ARROW / largest : 0;
    },

    /** Same idea as `scale`, for the elevation gauge. */
    elevScale() {
      if (!this.visible) return 0;
      const { design, asBuilt } = this.siteOffsets;
      const largest = Math.max(Math.abs(design?.z || 0), Math.abs(asBuilt?.z || 0));
      return largest > 0 ? MAX_ELEV_BAR / largest : 0;
    },

    panes() {
      if (!this.visible) return [];
      const { design, asBuilt } = this.siteOffsets;
      return [
        this.buildPane('design', 'Design', 'model', this.point.design, design, this.point.designAdjusted),
        this.buildPane('asBuilt', 'As-Built', 'scan', this.point.asBuilt, asBuilt, this.point.asBuiltAdjusted),
      ];
    },
  },
  methods: {
    /**
     * `offsetSite` arrives already converted (see `siteOffsets`); `raw` and
     * `adjusted` are still viewer-space POSITIONS and cross here. Both produce
     * NEW objects — `this.point` is the store's getter cache, and the marker
     * projection is drawing from those same objects.
     */
    buildPane(key, title, source, raw, offsetSite, adjusted) {
      const rawSite = viewerToSite(raw, this.unitTransform);
      const adjustedSite = viewerToSite(adjusted, this.unitTransform);

      const dx = offsetSite?.x || 0;
      const dy = offsetSite?.y || 0;
      const dz = offsetSite?.z || 0;

      // Screen Y grows downward; northing grows up. Negate dy so a +N offset
      // draws upward, matching the compass rose.
      const tipX = 60 + dx * this.scale;
      const tipY = 60 - dy * this.scale;

      const barPct = Math.min(Math.abs(dz) * this.elevScale, MAX_ELEV_BAR);
      const elevBarStyle = {
        height: `${barPct / 2}%`,
        // Anchored at the gauge's midline, growing up for +, down for −.
        [dz >= 0 ? 'bottom' : 'top']: '50%',
      };

      return {
        key,
        title,
        source,
        hasPlanShift: dx !== 0 || dy !== 0,
        tipX,
        tipY,
        elevBarStyle,
        planLabel: `${title} offset ${fmtOffset(dx)} east, ${fmtOffset(dy)} north`,
        offsetText: { z: fmtOffset(dz) },
        rows: AXES.map((axis) => ({
          axis: axis.label,
          raw: fmtPos(rawSite?.[axis.key]),
          // `?? null`, never `?? 0`: a null transform means the offset is not
          // known yet, and a printed "+0.0000" would claim it is zero.
          offset: fmtOffset(offsetSite?.[axis.key] ?? null),
          adjusted: fmtPos(adjustedSite?.[axis.key]),
        })),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.ocv {
  // `.work` is a flex column with overflow:hidden — without an explicit
  // flex:none this docked block gets squeezed by the viewer's flex:1 and its
  // content is silently clipped rather than shrinking the viewer.
  flex: none;
  border-top: 1px solid $vf-line;
  background: $vf-panel;
  padding: $vf-sm $vf-md;
}

.ocv__head {
  display: flex;
  align-items: baseline;
  gap: $vf-sm;
  margin-bottom: $vf-sm;
}

.ocv__title {
  font-family: $vf-font-heading;
  font-size: 12px;
  font-weight: 600;
  color: $vf-ink;
  letter-spacing: 0.02em;
}

.ocv__point {
  font-family: $vf-font-mono;
  font-size: 11px;
  color: $vf-ink-2;
}

// Every number below is site metres. Unlabelled, this panel's figures are
// indistinguishable from the millimetre deviations shown elsewhere in the UI.
.ocv__units {
  margin-left: auto;
  font-size: 10px;
  color: $vf-ink-3;
}

.ocv__panes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $vf-md;
}

.ocv__pane {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'head head'
    'plan rows'
    'elev rows'
    'legend legend';
  align-items: start;
  gap: $vf-xs $vf-sm;
  border: 1px solid $vf-line-soft;
  border-radius: $vf-r-md;
  padding: $vf-sm;
  background: $vf-panel-2;
}

.ocv__pane-head {
  grid-area: head;
  display: flex;
  align-items: baseline;
  gap: $vf-xs;
  margin-bottom: $vf-xs;
}

.ocv__pane-title {
  font-family: $vf-font-heading;
  font-size: 11px;
  font-weight: 600;
  color: $vf-brand-castleton;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ocv__pane-src {
  font-size: 10px;
  color: $vf-ink-3;
}

.ocv__plan {
  grid-area: plan;
  width: 96px;
  height: 96px;
}

.ocv__hair {
  stroke: $vf-line;
  stroke-width: 1;
}

.ocv__compass {
  font-family: $vf-font-mono;
  font-size: 8px;
  fill: $vf-ink-3;
}

.ocv__arrow {
  stroke: $vf-blue;
  stroke-width: 1.5;
  stroke-dasharray: 3 2;
}

.ocv__raw {
  fill: none;
  stroke: $vf-ink-3;
  stroke-width: 1.5;
}

.ocv__adj {
  stroke: $vf-primary;
  stroke-width: 1.5;

  circle {
    fill: $vf-primary;
    stroke: none;
  }
}

.ocv__elev {
  grid-area: elev;
  display: grid;
  grid-template-columns: auto 10px auto;
  align-items: center;
  gap: $vf-xs;
  margin-top: $vf-xs;
}

.ocv__elev-cap,
.ocv__elev-val {
  font-family: $vf-font-mono;
  font-size: 9px;
  color: $vf-ink-3;
}

.ocv__elev-val {
  color: $vf-ink-2;
}

.ocv__elev-track {
  position: relative;
  width: 10px;
  height: 44px;
  background: $vf-line-soft;
  border-radius: $vf-r-sm;
}

.ocv__elev-zero {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: $vf-line-strong;
}

.ocv__elev-bar {
  position: absolute;
  left: 1px;
  right: 1px;
  background: $vf-primary;
  border-radius: 1px;
}

.ocv__rows {
  grid-area: rows;
  margin: 0;
  display: grid;
  gap: 2px;
  align-self: center;
}

.ocv__row {
  display: grid;
  grid-template-columns: 20px 1fr 1fr 1fr;
  align-items: baseline;
  gap: $vf-xs;
}

.ocv__axis {
  font-family: $vf-font-mono;
  font-size: 9px;
  font-weight: 700;
  color: $vf-ink-3;
}

.ocv__raw-val,
.ocv__off-val,
.ocv__adj-val {
  margin: 0;
  font-family: $vf-font-mono;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.ocv__raw-val {
  color: $vf-ink-3;
}

.ocv__off-val {
  color: $vf-blue;
}

.ocv__adj-val {
  color: $vf-ink;
  font-weight: 600;
}

.ocv__legend {
  grid-area: legend;
  display: grid;
  grid-template-columns: 20px 1fr 1fr 1fr;
  gap: $vf-xs;
  margin-top: $vf-xs;
  padding-top: $vf-xs;
  border-top: 1px dashed $vf-line-soft;
  font-size: 9px;
  color: $vf-ink-3;
  text-align: right;

  span:first-child {
    grid-column: 2;
  }
}
</style>
